import _objectWithoutPropertiesLoose from "@babel/runtime/helpers/esm/objectWithoutPropertiesLoose";
const _excluded = ["onSuccess"],
  _excluded2 = ["data", "previousData"];
import { registerQuery, useQuery } from 'data-fetching-client';
import { VizSetting } from 'crm-index-visualization-core/core/model/vizSetting';
import { ViewDraft } from 'crm-index-visualization-core/core/model/viewDraft';
import { useClientStorage, useDraftContext } from 'crm-index-visualization-core/core/data/VizCoreProvider';
import { useMemo } from 'react';
import { useView } from './useView';
import { ViewAPI } from '../data/viewAPI';

// Internal query - now exported for cache updates
export const getVizSettingsQueryInternal = registerQuery({
  fieldName: 'vizcoreVizSettings',
  fetcher: ({
    draftContext,
    clientStorage,
    vizType,
    view
  }) => {
    // Detect bypass mode from the hash key
    const isBypassMode = draftContext.editHash === 'bypass-draft-active';
    if (isBypassMode) {
      if (!view) {
        return Promise.resolve(VizSetting.default(vizType));
      }
      try {
        return Promise.resolve(VizSetting.fromBEView(view, vizType));
      } catch (_unused) {
        return Promise.resolve(VizSetting.default(vizType));
      }
    }

    // If we have an editHash, try to get draft data from client cache first
    const {
      viewId,
      objectTypeId,
      editHash
    } = draftContext;
    return ViewDraft.fromClientCache(editHash !== null && editHash !== void 0 ? editHash : '', clientStorage).then(draft => {
      var _draft$vizSettingHash, _draft$vizSettingHash2;
      const vizSettingHash = (_draft$vizSettingHash = (_draft$vizSettingHash2 = draft.vizSettingHashesById) === null || _draft$vizSettingHash2 === void 0 ? void 0 : _draft$vizSettingHash2[vizType]) !== null && _draft$vizSettingHash !== void 0 ? _draft$vizSettingHash : '';
      return VizSetting.fromClientCache(vizSettingHash, clientStorage);
    }).catch(() => {
      // Failed to load from cache, fall through to VIEW backend
      if (!view) {
        throw new Error('View not found in useVizSettings');
      }
      return VizSetting.fromBEView(view, vizType);
    }).catch(() =>
    // Either View backend failed, or the settings are not in the view
    // Retrieve from another endpoint
    ViewAPI.getEffectiveVizSettings(viewId, objectTypeId, vizType, clientStorage)).catch(() =>
    // Ultimate fallback: return default settings to ensure UI never breaks
    VizSetting.default(vizType));
  },
  args: ['draftContext', 'clientStorage', 'vizType', 'view']
});
const EMPTY_OBJECT = {};

/**
 * Hook to fetch and manage visualization settings data, with transparent draft data handling.
 *
 * This hook is built on Data Fetching Client (DFC) and provides a clean API for accessing
 * visualization settings without exposing internal field keys.
 *
 * ## Visualization Settings
 *
 * VizSettings contain the configuration data for different visualization types (table, chart, etc.).
 * They include display preferences, column configurations, chart settings, and other visualization-specific
 * options. The `VizSetting` class provides a comprehensive data model that encapsulates all these aspects.
 *
 * ## Draft Mechanism
 *
 * The hook transparently handles draft data through an edit hash system:
 *
 * 1. When viz settings are edited, they're saved in client-side storage with a unique hash
 * 2. This hash is referenced in the ViewDraft composite structure via the global "editHash"
 * 3. The hook observes the editHash and returns draft data if present, falling back to cached
 *    or default settings if no draft exists
 *
 * ## Fallback Hierarchy
 *
 * 1. **Draft viz settings** - from client cache if editHash is present
 * 2. **Cached viz settings** - from client storage for the specific view/vizType
 * 3. **Default viz settings** - generated defaults for the visualization type
 *
 * @param vizType - The type of visualization to fetch settings for
 * @param options - Query options (excluding variables)
 * @throws {Error} If used outside of a VizCoreProvider
 * @returns {Object} Query result with viz settings data directly
 *   - data: VizSetting<T> | null - The current viz settings if available
 *   - loading: boolean - Whether the query is loading
 *   - error: ApolloError | undefined - Any query errors
 *
 * ## Basic Usage
 *
 * ```tsx
 * const { data: vizSettings, loading, error } = useVizSettings('table');
 *
 * if (loading) return <Spinner />;
 * if (error) return <ErrorMessage error={error} />;
 * if (!vizSettings) return <EmptyState />;
 *
 * return (
 *   <div>
 *     <h1>Settings for {vizSettings.vizType}</h1>
 *     <TableConfigPanel settings={vizSettings.config} />
 *   </div>
 * );
 * ```
 *
 * ## Editing Viz Settings
 *
 * Use the `useMutateVizSettings` hook to modify viz settings:
 *
 * ```tsx
 * const { data: vizSettings } = useVizSettings('table');
 * const [saveVizSettings, { loading }] = useMutateVizSettings('table');
 *
 * const updateColumnWidth = (columnId, width) => {
 *   if (vizSettings) {
 *     // Always use with() for immutable updates
 *     saveVizSettings(vizSettings.with({
 *       config: { ...vizSettings.config, columnWidths: { ...vizSettings.config.columnWidths, [columnId]: width } }
 *     }));
 *   }
 * };
 * ```
 *
 * ## Best Practices
 *
 * 1. **Use the `with()` method for immutable updates:**
 *    ```tsx
 *    // Good - creates a new VizSetting object
 *    saveVizSettings(vizSettings.with({ config: newConfig }));
 *    ```
 *
 * 2. **Access typed configuration data:**
 *    ```tsx
 *    const { data: tableSettings } = useVizSettings<TableConfig>('table');
 *    // tableSettings.config is now typed as TableConfig
 *    ```
 *
 * For more details on VizSetting properties and methods, see the VizSetting class JSDoc.
 *
 * @see VizSetting
 * @see useMutateVizSettings
 */
export function useVizSettings(vizType, options = EMPTY_OBJECT) {
  const [draftContext] = useDraftContext();
  const clientStorage = useClientStorage();
  const {
    data: {
      vizcoreCurrentView
    } = {},
    loading: viewLoading,
    error: viewError
  } = useView({
    onError: options.onError
  });

  // Memoize the query options to prevent unnecessary re-renders
  const queryOptions = useMemo(() => {
    const {
        onSuccess
      } = options,
      restOptions = _objectWithoutPropertiesLoose(options, _excluded);
    return Object.assign({}, restOptions, {
      onCompleted: responseData => {
        if (responseData !== null && responseData !== void 0 && responseData.vizcoreVizSettings && onSuccess) {
          onSuccess(responseData.vizcoreVizSettings);
        }
      },
      variables: {
        draftContext,
        clientStorage,
        vizType,
        view: vizcoreCurrentView
      }
    });
  }, [options, draftContext, clientStorage, vizType, vizcoreCurrentView]);
  const _useQuery = useQuery(getVizSettingsQueryInternal, queryOptions),
    {
      data,
      previousData
    } = _useQuery,
    rest = _objectWithoutPropertiesLoose(_useQuery, _excluded2);

  // Memoize based on actual viz settings content, not Apollo response objects
  const result = useMemo(() => {
    const transformedData = (data === null || data === void 0 ? void 0 : data.vizcoreVizSettings) || (previousData === null || previousData === void 0 ? void 0 : previousData.vizcoreVizSettings) || null;

    // If there's a view error, bubble it up as the viz settings error
    const finalError = viewError !== null && viewError !== void 0 ? viewError : rest.error;

    // Combine loading states: show loading if either view or viz settings query is loading
    const isLoading = viewLoading || rest.loading;
    return Object.assign({
      data: transformedData
    }, rest, {
      loading: isLoading,
      error: finalError
    });
  }, [data === null || data === void 0 ? void 0 : data.vizcoreVizSettings, // Only actual viz settings data
  previousData === null || previousData === void 0 ? void 0 : previousData.vizcoreVizSettings,
  // Only actual viz settings data
  rest, viewError, viewLoading]);
  return result;
}